<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Logo from '@leaflink/stash/Logo.vue';
  import Step from '@leaflink/stash/Step.vue';
  import Stepper from '@leaflink/stash/Stepper.vue';

  import { FeatureEnrollmentGoToStepArgs } from '@/models/featureEnrollment';

  import { useFeatureEnrollmentProvider } from './FeatureEnrollmentLayout.vue';

  const emits = defineEmits<{
    (e: 'onStepClick', args: FeatureEnrollmentGoToStepArgs): void;
  }>();

  const {
    featureName,
    steps,
    stepperOptions,
    sidenavRef,
    stepperRef,
    activeStepIndex,
    activeSubstepIndex,
    isSidenavActive,
  } = useFeatureEnrollmentProvider();

  const isStepCompleted = (index: number) => activeStepIndex.value > index;

  const onStepClick = ({ event, index, subIndex = -1 }: FeatureEnrollmentGoToStepArgs) => {
    const isActive = stepperRef.value?.isStepActive(index, subIndex);

    // Prevent firing the event if the step is already active
    if (isActive) return;

    // Prevent users moving forward by clicking on subsequent steps
    if (index > activeStepIndex.value) return;

    // Prevent users moving forward by clicking on subsequent substeps
    if (index === activeStepIndex.value && subIndex > activeSubstepIndex.value) return;

    emits('onStepClick', { event, index, subIndex });
  };
</script>

<template>
  <nav
    class="sidebar tw-flex tw-fixed tw-right-full tw-h-full tw-justify-center tw-w-[--sidenav-width] tw-z-control lg:tw-relative lg:tw-right-0"
    :class="{
      active: isSidenavActive,
    }"
    :hidden="!isSidenavActive"
    ref="sidenavRef"
  >
    <div class="tw-grid tw-content-start tw-gap-[2.4rem] tw-w-[180px] tw-mt-[5.25rem]">
      <Button
        class="tw-absolute tw-top-[36px] tw-left-12 tw-text-white lg:tw-hidden"
        icon
        data-test="button|sidenav-close"
        @click="isSidenavActive = !isSidenavActive"
      >
        <Icon name="close" />
      </Button>

      <Logo variant="lockup" color="white" />

      <h1 class="tw-m-0 tw-text-white">{{ featureName }}</h1>

      <slot>
        <Stepper
          v-if="steps"
          v-model:step="activeStepIndex"
          v-model:substep="activeSubstepIndex"
          ref="stepperRef"
          v-bind="stepperOptions"
        >
          <Step
            v-for="(step, index) in steps"
            :key="step.text"
            :text="step.text"
            :step="`${index + 1}`"
            :active="stepperRef?.isStepActive(index)"
            :completed="isStepCompleted(index)"
            @click="(event: Event) => onStepClick({ event, index })"
          >
            <Step
              v-for="(substep, subIndex) in step.substeps"
              :key="substep.text"
              :text="substep.text"
              :nested="true"
              :active="stepperRef?.isStepActive(index, subIndex)"
              @click.stop="(event: Event) => onStepClick({ event, index, subIndex })"
            />
          </Step>
        </Stepper>
      </slot>
    </div>
  </nav>
</template>

<style scoped>
  .sidebar {
    background-color: var(--color-purple);
    background-image: linear-gradient(to top, #211953 20%, transparent 200%),
      url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 348 1024'%3E%3Cpath fill='%23fff' d='m-109.35 576.473 127.655 42.762-85.716 255.884-127.655-42.762 85.716-255.884ZM352.192 1014.78c-77.358 4.36-142.761-47.476-142.761-124.957 0-77.48 60.753-135.476 142.761-134.57 77.471.854 141.144 50.517 142.171 133.99.987 79.811-62.299 121.027-142.171 125.537Z' opacity='.05'/%3E%3Cpath fill='%23fff' d='M209.056 921.615c3.124 55.36-33.975 102.165-89.422 102.165-55.448 0-96.952-43.477-96.304-102.165.612-55.44 36.152-101.007 95.889-101.742 57.115-.707 86.612 44.583 89.837 101.742ZM443.008 58.547C497.466-7.6 588.642-21.886 651.615 36.212c62.974 58.097 64.556 150.963 2.328 216.937-58.785 62.324-146.894 76.837-215.509 15.081-65.607-59.042-51.653-141.386 4.574-209.683ZM5.513 109.791c-54.103 22.301-80.08 79.217-60.796 132.338L25.76 465.364l192.129-69.749-81.042-223.235c-23.983-66.027-82.694-85.655-131.333-62.589ZM327.545 574.288c-28.773 46.596-17.229 103.996 26.542 133.679l183.942 124.742 107.359-158.311-183.942-124.741c-54.414-36.888-109.691-19.547-133.901 24.631Z' opacity='.05'/%3E%3Cpath fill='%23fff' d='M-134.451 525.17c71.89-69.05 183.907-78.227 269.55-11.723 89.715 69.667 113.541 182.545 55.247 263.781l-.159.205-76.868-60.006c31.27-50.694 13.136-99.553-32.058-134.648-45.218-35.113-98.575-29.784-136.113 4.203l-79.599-61.812ZM-68.686 81.368C-27.794 14.717 51.78-15.44 124.466 16.561c76.14 33.522 111.567 113.099 82.476 186.051l-.082.186-65.288-28.959c14.458-44.462-6.535-77.28-44.892-94.168-38.376-16.896-76.125-1.814-97.81 31.44l-67.556-29.743ZM-332.435 1021.92c10.088-86.536 80.529-155.011 175.264-157.781 99.24-2.902 178.073 59.974 183.93 147.171l.007.23-85.205 2.27c-7.1-51.574-46.496-74.441-96.489-72.979-50.018 1.463-83.038 34.725-89.456 78.519.008 0-87.589 2.56-88.051 2.57Z' opacity='.05'/%3E%3Cpath fill='%23fff' d='M243.832 729.877c11.331 24.624 39.68 36.327 61.566 26.255 21.885-10.073 33.387-37.154 22.057-61.778-12.721-27.635-102.891-111.703-102.891-111.703s6.544 119.585 19.268 147.226ZM316.271 175.056c-18.118-18.308-19.933-45.752-1.092-62.447 18.84-16.696 46.339-14.076 64.2 6.525 16.874 19.46 18.837 46.005-1.235 64.247-19.189 17.443-43.166 10.578-61.873-8.325ZM313.921 475.767c110.526 80.359 250.815 89.554 338.162-47.291L251.846 137.48c-80.349 110.513-48.452 257.928 62.075 338.287ZM6.208 720.987c9.64 58.212 50 103.557 118.337 87.941L89.64 598.132C31.436 607.77-3.43 662.775 6.208 720.987ZM296.454 6.081c-54.516 11.656-95.538 51.741-77.796 115.551l197.412-42.21c-11.654-54.508-65.1-84.997-119.616-73.341ZM258.539 445.284c-57.281-36.635-126.94-37.504-165.627 32.059L300.34 610.009c36.093-56.433 15.481-128.089-41.801-164.725Z' opacity='.05'/%3E%3Cpath fill='%23fff' d='M37.494 830.997c3.795-38.838-14.236-74.709-60.565-76.417l-13.744 140.639c38.833 3.795 70.513-25.384 74.309-64.222Z' opacity='.05'/%3E%3C/svg%3E");
    background-position: center;
    background-size: cover;
    transition: 0.2s;

    &::after {
      display: block;
      position: fixed;
      top: 0;
      left: var(--sidenav-width);
      width: calc(100vw + var(--sidenav-width));
      height: 100vh;
      background-color: #00000080;
      transition: 0.2s;
      opacity: 0;
      visibility: hidden;
      z-index: -1;
      content: '';
    }

    &.active {
      transform: translateX(100%);

      &::after {
        opacity: 1;
        visibility: visible;
      }
    }

    @media screen('lg') {
      &::after {
        display: none;
      }

      &.active {
        transform: translateX(0);
      }
    }
  }

  :deep(.stash-step__title) {
    white-space: normal;
    cursor: pointer;
  }
</style>
